import React, { useState, useEffect, useRef } from "react";
import { Search, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fetchAllPhoneCodes } from "@/lib/countryData";
import api from "@/lib/api";

export default function PhoneCountryCodeInput({ label, value, onChange, testId }) {
  const [dialCode, setDialCode] = useState(null);
  const [flag, setFlag] = useState("");
  const [number, setNumber] = useState("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [phoneCodes, setPhoneCodes] = useState([]);
  const wrapperRef = useRef(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    fetchAllPhoneCodes().then((codes) => {
      setPhoneCodes(codes);
      const fallback = codes.find((p) => p.value === "IN");
      api.get("/geo/currency").then((res) => {
        const cc = res.data.country_code;
        const match = codes.find((p) => p.value === cc) || fallback;
        setDialCode(match ? match.dialCode : "+91");
        setFlag(match ? match.flag : "");
      }).catch(() => { setDialCode("+91"); setFlag(fallback ? fallback.flag : ""); });
    }).catch(() => setDialCode("+91"));
  }, []);

  useEffect(() => {
    if (dialCode === null) return;
    onChange(number ? `${dialCode} ${number}` : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialCode, number]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = query.trim()
    ? phoneCodes.filter((p) => p.label.toLowerCase().includes(query.toLowerCase())).slice(0, 30)
    : phoneCodes.slice(0, 30);

  return (
    <div>
      {label && <Label className="text-sm font-medium text-[#1A1A1A] mb-1.5 block">{label}</Label>}
      <div className="flex h-10 items-stretch border border-[#E5E1DA] rounded-md bg-white focus-within:border-[var(--brand-primary)] focus-within:ring-1 focus-within:ring-[var(--brand-primary)] transition-colors">
        <div className="relative shrink-0 h-full" ref={wrapperRef}>
          <button
            type="button"
            data-testid={`${testId}-dial-code-btn`}
            onClick={() => setOpen(!open)}
            className="h-full pl-3 pr-2 rounded-l-md flex items-center gap-1.5 text-sm text-[#1A1A1A] hover:bg-[#FAF8F4] transition-colors"
          >
            {flag && <span className="text-base leading-none">{flag}</span>}
            <span className="font-medium whitespace-nowrap">{dialCode || "..."}</span>
            <ChevronDown size={14} className={`text-[var(--brand-secondary)] transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div className="absolute z-30 left-0 top-full mt-1.5 w-64 bg-white border border-[#E5E1DA] rounded-md shadow-lg max-h-56 overflow-y-auto">
              <div className="p-2 sticky top-0 bg-white border-b border-[#E5E1DA]">
                <div className="relative">
                  <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--brand-secondary)]" />
                  <Input
                    autoFocus
                    data-testid={`${testId}-dial-code-search`}
                    className="pl-7 h-8 text-xs"
                    placeholder="Search country"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
              </div>
              {filtered.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  data-testid={`${testId}-dial-code-option-${p.value}`}
                  onClick={() => { setDialCode(p.dialCode); setFlag(p.flag); setOpen(false); setQuery(""); }}
                  className="w-full text-left px-3 py-2 hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_90%)] flex items-center gap-2 text-xs border-b border-[#E5E1DA] last:border-0"
                >
                  <span>{p.flag}</span>
                  <span>{p.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="w-px bg-[#E5E1DA] my-2 shrink-0" />
        <input
          data-testid={testId}
          type="tel"
          placeholder="Phone number"
          value={number}
          onChange={(e) => setNumber(e.target.value.replace(/[^\d]/g, ""))}
          className="flex-1 min-w-0 h-full px-3 text-sm bg-transparent rounded-r-md outline-none border-0 placeholder:text-[var(--brand-secondary)]/60"
        />
      </div>
    </div>
  );
}
